import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    company: '',
    role: 'client',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await register(formData);
      toast.success(`Welcome, ${user.full_name}! Your account has been created.`);
      
      // Redirect based on role
      if (user.role === 'admin') {
        navigate('/dashboard/admin');
      } else if (user.role === 'vendor') {
        navigate('/dashboard/vendor');
      } else {
        navigate('/dashboard/client');
      }
    } catch (error) {
      toast.error('Registration failed. Email may already be in use.');
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center py-12" data-testid="register-page">
      <div className="w-full max-w-md px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo/Brand */}
          <Link to="/" className="flex justify-center mb-8">
            <div className="text-3xl font-bold tracking-tight">
              <span className="text-[#D4AF37]">NEHPETS</span>
              <span className="text-white">+</span>
            </div>
          </Link>

          {/* Register Form */}
          <div className="glass-effect p-8 space-y-6">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-medium mb-2">Create Account</h1>
              <p className="text-white/60 text-sm">Join us to start planning your perfect event</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Full Name *</label>
                <Input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                  data-testid="fullname-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Password *</label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                  data-testid="password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Phone</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+234 XXX XXX XXXX"
                  className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Company/Organization</label>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company Name"
                  className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                  data-testid="company-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-8 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] disabled:opacity-50"
                data-testid="register-submit-button"
              >
                {loading ? 'Creating Account...' : (
                  <>
                    Create Account
                    <UserPlus className="ml-2" size={16} />
                  </>
                )}
              </Button>
            </form>

            <div className="text-center pt-4 border-t border-white/5">
              <p className="text-white/60 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="login-link">
                  Login here
                </Link>
              </p>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <Link to="/" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;